import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    branchApi, 
    Branch, 
    City,
    BranchesListParams, 
    CreateBranchData, 
    UpdateBranchData,
    BranchesListResponse 
} from '../../services/branchApi';

// Async thunks
export const fetchBranches = createAsyncThunk(
    'branches/fetchBranches',
    async (params: BranchesListParams = {}) => {
        const response = await branchApi.getBranches(params);
        return response;
    }
);

export const fetchBranch = createAsyncThunk(
    'branches/fetchBranch',
    async (id: number) => {
        const response = await branchApi.getBranch(id);
        return response;
    }
);

export const createBranch = createAsyncThunk(
    'branches/createBranch',
    async (branchData: CreateBranchData) => {
        const response = await branchApi.createBranch(branchData);
        return response;
    }
);

export const updateBranch = createAsyncThunk(
    'branches/updateBranch',
    async ({ id, branchData }: { id: number; branchData: UpdateBranchData }) => {
        const response = await branchApi.updateBranch(id, branchData);
        return response;
    }
);

export const deleteBranch = createAsyncThunk(
    'branches/deleteBranch',
    async (id: number) => {
        await branchApi.deleteBranch(id);
        return id;
    }
);

export const toggleBranchStatus = createAsyncThunk(
    'branches/toggleBranchStatus',
    async (id: number) => {
        const response = await branchApi.toggleBranchStatus(id);
        return response;
    }
);

export const fetchBranchCities = createAsyncThunk(
    'branches/fetchBranchCities',
    async () => {
        const response = await branchApi.getCities('1'); // Fetch only active cities
        return response;
    }
);

// State interface
interface BranchState {
    branches: Branch[];
    selectedBranch: Branch | null;
    allCities: City[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        status: string;
        query: string;
        city_id: string;
    };
    loading: boolean;
    error: string | null;
}

// Initial state
const initialState: BranchState = {
    branches: [],
    selectedBranch: null,
    allCities: [],
    pagination: null,
    filters: {
        status: '',
        query: '',
        city_id: '',
    },
    loading: false,
    error: null,
};

// Slice
const branchSlice = createSlice({
    name: 'branches',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<Partial<BranchState['filters']>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                status: '',
                query: '',
                city_id: '',
            };
        },
        clearError: (state) => {
            state.error = null;
        },
        setSelectedBranch: (state, action: PayloadAction<Branch | null>) => {
            state.selectedBranch = action.payload;
        },
    },
    extraReducers: (builder) => {
        // fetchBranches
        builder
            .addCase(fetchBranches.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBranches.fulfilled, (state, action: PayloadAction<BranchesListResponse>) => {
                state.loading = false;
                state.branches = action.payload.branches;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchBranches.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch branches';
            });

        // fetchBranch
        builder
            .addCase(fetchBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBranch.fulfilled, (state, action: PayloadAction<Branch>) => {
                state.loading = false;
                state.selectedBranch = action.payload;
            })
            .addCase(fetchBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch branch';
            });

        // createBranch
        builder
            .addCase(createBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBranch.fulfilled, (state, action: PayloadAction<Branch>) => {
                state.loading = false;
                state.branches.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create branch';
            });

        // updateBranch
        builder
            .addCase(updateBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBranch.fulfilled, (state, action: PayloadAction<Branch>) => {
                state.loading = false;
                const index = state.branches.findIndex(branch => branch.id === action.payload.id);
                if (index !== -1) {
                    state.branches[index] = action.payload;
                }
                if (state.selectedBranch?.id === action.payload.id) {
                    state.selectedBranch = action.payload;
                }
            })
            .addCase(updateBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update branch';
            });

        // deleteBranch
        builder
            .addCase(deleteBranch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteBranch.fulfilled, (state, action: PayloadAction<number>) => {
                state.loading = false;
                state.branches = state.branches.filter(branch => branch.id !== action.payload);
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteBranch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete branch';
            });

        // toggleBranchStatus
        builder
            .addCase(toggleBranchStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleBranchStatus.fulfilled, (state, action: PayloadAction<{ id: number; name: string; status: number }>) => {
                state.loading = false;
                const index = state.branches.findIndex(branch => branch.id === action.payload.id);
                if (index !== -1) {
                    state.branches[index].status = action.payload.status;
                }
                if (state.selectedBranch?.id === action.payload.id) {
                    state.selectedBranch.status = action.payload.status;
                }
            })
            .addCase(toggleBranchStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to toggle branch status';
            });

        // fetchBranchCities
        builder
            .addCase(fetchBranchCities.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBranchCities.fulfilled, (state, action: PayloadAction<City[]>) => {
                state.loading = false;
                state.allCities = action.payload;
            })
            .addCase(fetchBranchCities.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch cities';
            });
    },
});

export const { setFilters, clearFilters, clearError, setSelectedBranch } = branchSlice.actions;
export default branchSlice.reducer;
